import React, { useState } from 'react';
import api from '@/api/api';
import { SupplierRecommendationForm as RecommendationData } from '@/models/customersTypes';
import FormField, {
    EMAIL_INVALID_TEXT,
    EMAIL_REGEX,
    REQUIRED_TEXT,
    SEND_FAILED_TEXT,
    SHORT_MAX_LENGTH,
    SubmitButton,
    TEXT_MAX_LENGTH,
    inputClass,
} from '@/components/Forms/FormField';
import StoreSelect, { storeLabel, useStores } from '@/components/Forms/StoreSelect';
import { track } from '@/helpers/analytics';

const EMPTY_FORM: RecommendationData = {
    store: '',
    supplier_name: '',
    supplier_location: '',
    products: '',
    website: '',
    reason: '',
    email: '',
};

type TextKey = Exclude<keyof RecommendationData, 'store'>;

const TEXT_FIELDS: { key: TextKey; label: string; required?: boolean; type?: string; multiline?: boolean }[] = [
    { key: 'supplier_name', label: 'Wie heißt der Anbieter?', required: true },
    { key: 'supplier_location', label: 'In welchem Ort ist der Anbieter?', required: true },
    { key: 'products', label: 'Welche Produkte wünschst du dir von dort?', required: true, multiline: true },
    { key: 'website', label: 'Website oder öffentlich erreichbarer Geschäftskontakt (optional)' },
    { key: 'reason', label: 'Was gefällt dir an diesem Anbieter? (optional)', multiline: true },
    { key: 'email', label: 'Deine E-Mail-Adresse für Rückfragen (optional)', type: 'email' },
];

function validate(form: RecommendationData) {
    const errors: Record<string, string> = {};
    if (!form.store.trim()) errors.store = 'Bitte wähle einen Standort aus.';
    TEXT_FIELDS.forEach(({ key, required }) => {
        if (required && !form[key].trim()) errors[key] = REQUIRED_TEXT;
    });
    if (form.email.trim() && !EMAIL_REGEX.test(form.email.trim())) errors.email = EMAIL_INVALID_TEXT;
    return errors;
}

const SupplierRecommendationForm: React.FC = () => {
    const { stores, failed } = useStores();
    const [form, setForm] = useState<RecommendationData>(EMPTY_FORM);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

    const update = <K extends keyof RecommendationData>(key: K, value: RecommendationData[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validationErrors = validate(form);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;

        setStatus('sending');
        try {
            await api.interests.sendSupplierRecommendation(form);
            track('supplier-recommendation-sent', { store: storeLabel(form.store) });
            setStatus('sent');
            setForm(EMPTY_FORM);
        } catch {
            track('form-error', { form: 'supplier-recommendation' });
            setStatus('error');
        }
    };

    if (status === 'sent') {
        return (
            <div className="rounded-2xl bg-white p-6 shadow-md" role="status">
                <h3 className="mb-2 text-2xl font-bold">Danke für deinen Tipp.</h3>
                <p>Deine Empfehlung hilft uns, passende Anbieter aus der Region kennenzulernen.</p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} noValidate className="space-y-5 rounded-2xl bg-white p-6 shadow-md" data-testid="supplier-recommendation-form">
            <FormField label="Für welchen Yobsti ist dein Vorschlag?" htmlFor="recommendation-store" required error={errors.store}>
                <StoreSelect
                    id="recommendation-store"
                    value={form.store}
                    stores={stores}
                    failed={failed}
                    onChange={(store) => update('store', store)}
                />
            </FormField>

            {TEXT_FIELDS.map(({ key, label, required, type = 'text', multiline }) => (
                <FormField key={key} label={label} htmlFor={`recommendation-${key}`} required={required} error={errors[key]}>
                    {multiline ? (
                        <textarea
                            id={`recommendation-${key}`}
                            rows={3}
                            value={form[key]}
                            onChange={(e) => update(key, e.target.value)}
                            maxLength={TEXT_MAX_LENGTH}
                            className={inputClass}
                        />
                    ) : (
                        <input
                            id={`recommendation-${key}`}
                            type={type}
                            value={form[key]}
                            onChange={(e) => update(key, e.target.value)}
                            maxLength={SHORT_MAX_LENGTH}
                            className={inputClass}
                        />
                    )}
                </FormField>
            ))}

            <p className="text-sm text-gray-600">
                * Pflichtfelder. Bitte gib keine privaten Kontaktdaten Dritter an. Mehr dazu in unserer{' '}
                <a href="/datenschutz" className="underline">Datenschutzerklärung</a>.
            </p>

            {status === 'error' && <p className="text-red-600" role="alert">{SEND_FAILED_TEXT}</p>}

            <SubmitButton sending={status === 'sending'}>Empfehlung senden</SubmitButton>
        </form>
    );
};

export default SupplierRecommendationForm;
